import React, { useCallback, useMemo, useState } from 'react';
import { useGetList, useGetMany } from 'react-admin';
import { useQuery, useQueryClient } from 'react-query';
import { CustomerEvent, CustomerEventType } from '../types';
import {
  SmsBubble,
  PhoneEventBubble,
  EmailEventBubble,
  NoteBubble,
  SystemEventBubble,
  DateDivider,
} from '../funerals/components/EventBubbles';
import { EmailThreadCard, EmailThreadModal } from '../funerals/components/EmailThread';
import {
  EmailHeaders,
  StoredAttachment,
  communicationEmailIds,
  groupEmailThreads,
  mergeStoredAttachments,
} from '../funerals/emailThreads';
import { apiFetch } from '../lib/apiFetch';

interface CommunicationRow {
  id: string;
  client_id: number;
  channel: 'call' | 'sms' | 'email' | 'voicemail' | 'form';
  direction: 'inbound' | 'outbound';
  occurred_at: string;
  from_identifier?: string;
  to_identifier?: string;
  agent_name?: string;
  subject?: string;
  body?: string;
  metadata?: Record<string, any>;
  source_table?: string;
  source_id?: string;
  call_status?: 'answered' | 'missed' | 'voicemail' | null;
  duration_seconds?: number | null;
  lead_id?: string | null;
  gmail_thread_id?: string | null;
  body_html?: string | null;
  email_cc?: string[] | null;
  rfc_message_id?: string | null;
  email_in_reply_to?: string | null;
  attachments?: EmailHeaders['attachments'];
}

interface NoteRow {
  id: string;
  lead_id?: string;
  funeral_id?: number;
  client_id?: number;
  message: string;
  user_name?: string;
  created_at: string;
}

export function mapToCustomerEvent(row: CommunicationRow): CustomerEvent {
  let type: CustomerEventType;

  if (row.channel === 'sms' && row.direction === 'inbound') {
    type = CustomerEventType.CUSTOMER_MESSAGE;
  } else if (row.channel === 'sms' && row.direction === 'outbound') {
    type = CustomerEventType.TEXT_MESSAGE;
  } else if (row.channel === 'call' || row.channel === 'voicemail') {
    type = CustomerEventType.PHONE_CALL;
  } else {
    type = CustomerEventType.EMAIL;
  }

  return {
    id: row.id,
    funeral_id: 0,
    timestamp: row.occurred_at,
    type,
    contact_detail: row.direction === 'outbound' ? row.to_identifier : row.from_identifier,
    subject: row.subject,
    message: row.body,
    user_name: row.agent_name,
    source_id: row.source_table === 'call_sessions' ? row.source_id : undefined,
    metadata: row.metadata,
    // An email or form with no direction renders as ours, on the right.
    ...(type === CustomerEventType.EMAIL
      ? { direction: row.direction, gmail_thread_id: row.gmail_thread_id ?? undefined, body_html: row.body_html }
      : {}),
    ...(type === CustomerEventType.PHONE_CALL
      ? { direction: row.direction, call_status: row.call_status, duration_seconds: row.duration_seconds }
      : {}),
  };
}

// Aura-app writes "Arrangement Page Viewed" / "Obituary Page Viewed" /
// "Thank-You Page Viewed" to the notes table when a customer lands on one of
// those pages. They're not really internal notes — detect them and re-type
// so they render as a small system badge instead of a yellow note card.
const PAGE_VIEW_NOTE_RE = /^(Arrangement|Obituary|Thank-You) Page Viewed$/i;

function noteToCustomerEvent(note: NoteRow): CustomerEvent {
  const isPageView = PAGE_VIEW_NOTE_RE.test(note.message || '');
  if (isPageView) {
    return {
      id: note.id,
      funeral_id: note.funeral_id || 0,
      timestamp: note.created_at,
      type: CustomerEventType.PAGE_VIEW,
      subject: note.message,
      message: note.message,
    };
  }
  return {
    id: note.id,
    funeral_id: note.funeral_id || 0,
    timestamp: note.created_at,
    type: CustomerEventType.INTERNAL_NOTE,
    message: note.message,
    user_name: note.user_name,
  };
}

function isSameDay(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10);
}


interface ContactActivityFeedProps {
  clientIds: number[];
  /** When provided, also fetches notes for this lead */
  leadId?: string;
  /** When provided, also fetches notes for this funeral */
  funeralId?: number;
  /** Extra events to merge in (e.g. optimistic messages) */
  optimisticEvents?: CustomerEvent[];
}

export function ContactActivityFeed({ clientIds, leadId, funeralId, optimisticEvents = [] }: ContactActivityFeedProps) {
  const filterValue = clientIds.length === 1
    ? clientIds[0]
    : `(${clientIds.join(',')})`;

  const filterKey = clientIds.length === 1 ? 'client_id' : 'client_id@in';

  const { data: communications, isLoading: commsLoading } = useGetList<CommunicationRow>(
    'communications',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'occurred_at', order: 'ASC' },
      filter: { [filterKey]: filterValue },
    },
    { enabled: clientIds.length > 0 },
  );

  // A message filed on the lead but not on this person: a relative replying from
  // another address, or a Gmail reply whose recipient isn't a known client.
  const { data: leadCommunications } = useGetList<CommunicationRow>(
    'communications',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'occurred_at', order: 'ASC' },
      filter: { lead_id: leadId },
    },
    { enabled: !!leadId },
  );

  const rows = useMemo(() => {
    const byId = new Map((communications || []).map((r) => [r.id, r]));
    for (const r of leadCommunications || []) {
      // A row with a person shows through that person's filter chip instead.
      if (r.client_id == null && !byId.has(r.id)) byId.set(r.id, r);
    }
    return [...byId.values()];
  }, [communications, leadCommunications]);

  const { data: threadClients } = useGetMany<{ id: number; name: string; email?: string | null }>(
    'clients',
    { ids: clientIds },
    { enabled: clientIds.length > 0 },
  );

  // Real emails only: a form submission is a one-off card, with nothing to open.
  const emailRows = useMemo(() => rows.filter((r) => r.channel === 'email'), [rows]);
  const attachmentRowIds = useMemo(
    () =>
      communicationEmailIds(
        emailRows
          .filter((r) => r.attachments?.length || r.metadata?.has_attachments)
          .map(mapToCustomerEvent),
      ),
    [emailRows],
  );
  // Stored files come from the backend: the table is staff-only. A failure here
  // only costs the download links, not the thread.
  const { data: storedAttachments } = useQuery(
    ['contact_feed_email_attachments', attachmentRowIds.join(',')],
    () =>
      apiFetch(`email-attachments?communication_ids=${encodeURIComponent(attachmentRowIds.join(','))}`).catch(
        (err) => {
          console.error('Email attachments failed to load:', err);
          return [];
        },
      ) as Promise<StoredAttachment[]>,
    { enabled: attachmentRowIds.length > 0, staleTime: 60 * 1000 },
  );

  const threads = useMemo(() => {
    const headers: EmailHeaders[] = emailRows.map((r) => ({
      id: r.id,
      from_identifier: r.from_identifier ?? null,
      to_identifier: r.to_identifier ?? null,
      email_cc: r.email_cc ?? null,
      rfc_message_id: r.rfc_message_id ?? null,
      email_in_reply_to: r.email_in_reply_to ?? null,
      attachments: r.attachments ?? null,
    }));
    const merged = mergeStoredAttachments(headers, storedAttachments || []);
    const events = [
      ...emailRows.map(mapToCustomerEvent),
      ...optimisticEvents.filter((e) => e.type === CustomerEventType.EMAIL),
    ];
    return groupEmailThreads(events, new Map(merged.map((h) => [String(h.id), h])));
  }, [emailRows, storedAttachments, optimisticEvents]);

  // One card per thread, at its newest message.
  const { threadByLatestId, olderThreadIds } = useMemo(() => {
    const latest = new Map(threads.map((t) => [String(t.latest.event.id), t]));
    const older = new Set(
      threads.flatMap((t) => t.messages.map((m) => String(m.event.id))).filter((id) => !latest.has(id)),
    );
    return { threadByLatestId: latest, olderThreadIds: older };
  }, [threads]);

  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const openThread = useMemo(
    () => threads.find((t) => t.id === openThreadId) ?? null,
    [threads, openThreadId],
  );
  const queryClient = useQueryClient();
  const refreshAfterReply = useCallback(() => {
    void queryClient.invalidateQueries(['communications']);
  }, [queryClient]);
  const openThreadRow = rows.find((r) => r.id === String(openThread?.latest.event.id));

  // Fetch notes for the entity
  const noteFilter: Record<string, any> = {};
  if (leadId) noteFilter.lead_id = leadId;
  else if (funeralId) noteFilter.funeral_id = funeralId;

  const hasNoteFilter = Object.keys(noteFilter).length > 0;

  const { data: notes, isLoading: notesLoading } = useGetList<NoteRow>(
    'notes',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'created_at', order: 'ASC' },
      filter: noteFilter,
    },
    { enabled: hasNoteFilter },
  );

  const isLoading = commsLoading || (hasNoteFilter && notesLoading);

  const elements = useMemo(() => {
    const commEvents = rows.map((row) => mapToCustomerEvent(row));
    const noteEvents = (notes || []).map((note) => noteToCustomerEvent(note));

    // Merge and sort chronologically (metadata travels on the event itself)
    const allEvents = [...commEvents, ...noteEvents, ...optimisticEvents]
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    if (allEvents.length === 0) return [];

    const nodes: React.ReactNode[] = [];
    let lastDate = '';

    for (const event of allEvents) {
      if (olderThreadIds.has(String(event.id))) continue;
      const eventDate = event.timestamp;
      if (!lastDate || !isSameDay(lastDate, eventDate)) {
        nodes.push(<DateDivider key={`date-${eventDate}`} date={eventDate} />);
        lastDate = eventDate;
      }

      if (event.type === CustomerEventType.INTERNAL_NOTE) {
        nodes.push(<NoteBubble key={event.id} event={event} />);
      } else if (event.type === CustomerEventType.EMAIL) {
        const thread = threadByLatestId.get(String(event.id));
        nodes.push(
          thread ? (
            <EmailThreadCard
              key={event.id}
              thread={thread}
              clients={threadClients || []}
              partners={[]}
              onOpen={(t) => setOpenThreadId(t.id)}
            />
          ) : (
            <EmailEventBubble key={event.id} event={event} />
          ),
        );
      } else if (event.type === CustomerEventType.PHONE_CALL) {
        nodes.push(<PhoneEventBubble key={event.id} event={event} />);
      } else if (event.type === CustomerEventType.PAGE_VIEW) {
        nodes.push(<SystemEventBubble key={event.id} event={event} />);
      } else {
        nodes.push(<SmsBubble key={event.id} event={event} />);
      }
    }

    return nodes;
  }, [rows, notes, optimisticEvents, threadByLatestId, olderThreadIds, threadClients]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-20 text-sm text-muted-foreground">
        Loading activity...
      </div>
    );
  }

  if (elements.length === 0) {
    return (
      <div className="flex items-center justify-center h-20 text-sm text-muted-foreground">
        No activity yet
      </div>
    );
  }

  return (
    <div className="px-6 py-2 space-y-1">
      {elements}
      <EmailThreadModal
        thread={openThread}
        onClose={() => setOpenThreadId(null)}
        leadId={leadId ?? openThreadRow?.lead_id ?? undefined}
        clientId={openThreadRow?.client_id ?? (clientIds.length === 1 ? clientIds[0] : undefined)}
        clients={threadClients || []}
        partners={[]}
        caseAddress={null}
        onSent={refreshAfterReply}
      />
    </div>
  );
}
